/**
 * GENESIS - Artificial Life Simulator
 * Main Application Component with Multiplayer Support
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useToast } from './contexts/ToastContext';
import { useSimulationStore } from './stores/simulationStore';
import { useSocket } from './hooks/useSocket';
import { AdminPanel } from './components/admin';
import type {
  WorldState,
  SimulationStatus,
  CreatureDetails,
  BrainData,
  UserSession,
  PlayersUpdate,
  MyCreatureState,
  GuidanceSettings,
  CreatureDataWithOwner,
} from './types/simulation';
import { LoginPage } from './components/LoginPage';
import { WorldRenderer } from './components/WorldRenderer';
import { StatsPanel } from './components/StatsPanel';
import { CreaturePanel } from './components/CreaturePanel';
import { LoadingSpinner } from './components/ui';
import { BrainVisualizer } from './components/BrainVisualizer';
import { MyCreaturePanel } from './components/MyCreaturePanel';
import { GuidanceControls } from './components/GuidanceControls';
import { PlayerList } from './components/PlayerList';
import { Leaderboard } from './components/Leaderboard';
import { Minimap } from './components/Minimap';
import { DeathOverlay } from './components/DeathOverlay';
import { Tutorial } from './components/Tutorial';
import { KeyboardHelp } from './components/KeyboardHelp';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';

function App() {
  // Auth state
  const { user, isAuthenticated, isAdmin, isLoading: authLoading, logout } = useAuth();

  // Admin panel state
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Keyboard help state
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMobileSidebar, setActiveMobileSidebar] = useState<'left' | 'right'>('left');

  // Store actions
  const setConnected = useSimulationStore((state) => state.setConnected);
  const setSession = useSimulationStore((state) => state.setSession);
  const setMyCreature = useSimulationStore((state) => state.setMyCreature);
  const setGuidance = useSimulationStore((state) => state.setGuidance);
  const setWaypoint = useSimulationStore((state) => state.setWaypoint);
  const setPlayers = useSimulationStore((state) => state.setPlayers);
  const setSimulationStatus = useSimulationStore((state) => state.setSimulationStatus);
  const setWorldState = useSimulationStore((state) => state.setWorldState);
  const setCreatureDetails = useSimulationStore((state) => state.setCreatureDetails);
  const setCreatureBrain = useSimulationStore((state) => state.setCreatureBrain);
  const setSelectedCreature = useSimulationStore((state) => state.setSelectedCreature);
  const setFollowCreature = useSimulationStore((state) => state.setFollowCreature);
  const setCamera = useSimulationStore((state) => state.setCamera);

  // Store state
  const isConnected = useSimulationStore((state) => state.isConnected);
  const tick = useSimulationStore((state) => state.tick);
  const actualTPS = useSimulationStore((state) => state.actualTPS);
  const selectedCreatureId = useSimulationStore((state) => state.selectedCreatureId);
  const followCreature = useSimulationStore((state) => state.followCreature);
  const creatures = useSimulationStore((state) => state.creatures);
  const myCreatureId = useSimulationStore((state) => state.myCreatureId);
  const connectedCount = useSimulationStore((state) => state.connectedCount);

  // Toast notifications
  const toast = useToast();

  // Track previous creature state for death/spawn detection
  const prevCreatureStateRef = useRef<{ alive: boolean; id: string | null }>({
    alive: false,
    id: null,
  });

  // Socket connection with callbacks
  const {
    isConnected: socketIsConnected,
    subscribeToState,
    selectCreature,
    deselectCreature,
    spawnMyCreature,
    spawnFood,
    setGuidance: sendGuidance,
    setWaypoint: sendWaypoint,
    clearWaypoint: sendClearWaypoint,
  } = useSocket({
    autoConnect: true,
    onWorldState: useCallback(
      (state: WorldState) => {
        setWorldState(
          state.creatures as CreatureDataWithOwner[],
          state.food,
          state.stats,
          state.tick
        );
      },
      [setWorldState]
    ),
    onSimulationStatus: useCallback(
      (status: SimulationStatus) => {
        setSimulationStatus(status);
      },
      [setSimulationStatus]
    ),
    onCreatureDetails: useCallback(
      (details: CreatureDetails | null) => {
        setCreatureDetails(details);
      },
      [setCreatureDetails]
    ),
    onCreatureBrain: useCallback(
      (brain: BrainData | null) => {
        setCreatureBrain(brain);
      },
      [setCreatureBrain]
    ),
    onSessionState: useCallback(
      (session: UserSession) => {
        setSession(session);
      },
      [setSession]
    ),
    onPlayersUpdate: useCallback(
      (update: PlayersUpdate) => {
        setPlayers(update.players, update.leaderboard, update.connectedCount);
      },
      [setPlayers]
    ),
    onMyCreature: useCallback(
      (state: MyCreatureState) => {
        const prev = prevCreatureStateRef.current;

        // Detect spawn: was dead/no creature, now alive
        if (state.alive && state.id && (!prev.alive || prev.id !== state.id)) {
          toast.success('Criatura creada', 'Tu criatura ha nacido en el mundo');
        }

        // Detect death: was alive, now dead
        if (!state.alive && prev.alive && prev.id) {
          toast.error('Criatura muerta', 'Tu criatura ha muerto. Puedes crear otra.');
        }

        // Update ref for next comparison
        prevCreatureStateRef.current = { alive: state.alive, id: state.id };

        setMyCreature(state);
        // Auto-select and follow my creature when spawned
        if (state.id && state.alive) {
          setSelectedCreature(state.id);
          setFollowCreature(true);
        }
      },
      [setMyCreature, setSelectedCreature, setFollowCreature, toast]
    ),
    onGuidanceUpdated: useCallback(
      (guidance: GuidanceSettings) => {
        setGuidance(guidance);
      },
      [setGuidance]
    ),
    onWaypointSet: useCallback(
      (point: { x: number; y: number }) => {
        setWaypoint(point);
      },
      [setWaypoint]
    ),
    onWaypointCleared: useCallback(() => {
      setWaypoint(null);
    }, [setWaypoint]),
  });

  // Track connection status in store and notify on changes
  const prevConnectedRef = useRef<boolean | null>(null);
  useEffect(() => {
    // Show toast on connection changes (skip initial mount)
    if (prevConnectedRef.current !== null && prevConnectedRef.current !== socketIsConnected) {
      if (socketIsConnected) {
        toast.success('Conectado', 'Conexion con el servidor establecida');
      } else {
        toast.warning('Desconectado', 'Se perdio la conexion con el servidor');
      }
    }
    prevConnectedRef.current = socketIsConnected;
    setConnected(socketIsConnected);
  }, [socketIsConnected, setConnected, toast]);

  // Subscribe to state updates when connected
  useEffect(() => {
    if (isConnected) {
      subscribeToState();
    }
  }, [isConnected, subscribeToState]);

  // Sync creature selection via socket when myCreatureId changes
  useEffect(() => {
    if (myCreatureId && isConnected) {
      selectCreature(myCreatureId);
    }
  }, [myCreatureId, isConnected, selectCreature]);

  // Handle creature selection
  const handleCreatureClick = useCallback(
    (id: string) => {
      setSelectedCreature(id);
      selectCreature(id);
    },
    [setSelectedCreature, selectCreature]
  );

  // Handle background click - set waypoint if has creature, else spawn food
  const handleBackgroundClick = useCallback(
    (x: number, y: number) => {
      if (myCreatureId) {
        // Set waypoint for my creature
        sendWaypoint(x, y);
      } else {
        // Spawn food (legacy behavior)
        spawnFood(x, y);
      }
    },
    [myCreatureId, sendWaypoint, spawnFood]
  );

  // Handle deselect
  const handleDeselect = useCallback(() => {
    setSelectedCreature(null);
    deselectCreature();
  }, [setSelectedCreature, deselectCreature]);

  // Handle follow toggle
  const handleFollowToggle = useCallback(() => {
    setFollowCreature(!followCreature);
  }, [followCreature, setFollowCreature]);

  // Follow creature camera update
  useEffect(() => {
    if (followCreature && selectedCreatureId) {
      const creature = creatures.find((c) => c.id === selectedCreatureId);
      if (creature) {
        setCamera(creature.x, creature.y);
      }
    }
  }, [followCreature, selectedCreatureId, creatures, setCamera]);

  // Handle spawn my creature
  const handleSpawnMyCreature = useCallback(() => {
    spawnMyCreature();
  }, [spawnMyCreature]);

  // Handle follow my creature
  const handleFollowMyCreature = useCallback(() => {
    if (myCreatureId) {
      setSelectedCreature(myCreatureId);
      selectCreature(myCreatureId);
      setFollowCreature(true);
    }
  }, [myCreatureId, setSelectedCreature, selectCreature, setFollowCreature]);

  // Handle guidance change
  const handleGuidanceChange = useCallback(
    (guidance: Partial<GuidanceSettings>) => {
      sendGuidance(guidance);
    },
    [sendGuidance]
  );

  // Handle clear waypoint
  const handleClearWaypoint = useCallback(() => {
    sendClearWaypoint();
  }, [sendClearWaypoint]);

  // Handle minimap navigation
  const handleMinimapNavigate = useCallback(
    (x: number, y: number) => {
      setCamera(x, y);
      setFollowCreature(false);
    },
    [setCamera, setFollowCreature]
  );

  // Handle keyboard help toggle
  const handleShowKeyboardHelp = useCallback(() => {
    setShowKeyboardHelp((prev) => !prev);
  }, []);

  // Register keyboard shortcuts
  useKeyboardShortcuts({
    onFollowToggle: handleFollowToggle,
    onDeselect: handleDeselect,
    onShowHelp: handleShowKeyboardHelp,
    enabled: isAuthenticated && !showAdminPanel,
  });

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="app-loading">
        <LoadingSpinner text="Cargando..." />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show admin panel if toggled
  if (showAdminPanel && isAdmin) {
    return <AdminPanel onBack={() => setShowAdminPanel(false)} />;
  }

  return (
    <div className="app" role="application" aria-label="GENESIS - Simulador de Vida Artificial">
      {/* Skip link for keyboard navigation */}
      <a href="#main-content" className="skip-link">
        Saltar al contenido principal
      </a>
      <header className="header" role="banner">
        <button
          className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Cerrar menu' : 'Abrir menu'}
          aria-expanded={mobileMenuOpen}
        >
          <span className="hamburger-icon" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
        <h1>GENESIS</h1>
        <span className="subtitle">Simulador de Vida Artificial</span>
        <div className="header-info">
          {user && (
            <div className="user-info" aria-label={`Usuario: ${user.name || user.email}`}>
              {user.image && <img src={user.image} alt={`Avatar de ${user.name || 'usuario'}`} className="user-avatar" />}
              <span className="user-name">{user.name || user.email}</span>
            </div>
          )}
          <span className="players-count" aria-label={`${connectedCount} jugadores conectados`}>
            {connectedCount} jugadores
          </span>
          <div
            className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}
            role="status"
            aria-live="polite"
            aria-label={isConnected ? 'Estado: conectado al servidor' : 'Estado: desconectado del servidor'}
          >
            <span className="connection-dot" aria-hidden="true" />
            <span className="connection-text">{isConnected ? 'Conectado' : 'Desconectado'}</span>
          </div>
          {isAdmin && (
            <button
              className="admin-button"
              onClick={() => setShowAdminPanel(true)}
              aria-label="Abrir panel de administracion"
            >
              <span aria-hidden="true">⚙</span> Admin
            </button>
          )}
          <button
            className="logout-button"
            onClick={logout}
            aria-label="Cerrar sesion y salir"
          >
            Salir
          </button>
        </div>
      </header>

      <main className="main" role="main" id="main-content">
        {/* Mobile sidebar tabs */}
        {mobileMenuOpen && (
          <div className="mobile-sidebar-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeMobileSidebar === 'left'}
              className={activeMobileSidebar === 'left' ? 'active' : ''}
              onClick={() => setActiveMobileSidebar('left')}
            >
              Mi Criatura
            </button>
            <button
              role="tab"
              aria-selected={activeMobileSidebar === 'right'}
              className={activeMobileSidebar === 'right' ? 'active' : ''}
              onClick={() => setActiveMobileSidebar('right')}
            >
              Estadisticas
            </button>
          </div>
        )}

        <aside
          className={`sidebar left ${mobileMenuOpen && activeMobileSidebar === 'left' ? 'mobile-visible' : ''}`}
          role="complementary"
          aria-label="Controles de criatura"
        >
          <MyCreaturePanel
            onSpawn={handleSpawnMyCreature}
            onFollowMyCreature={handleFollowMyCreature}
          />
          <GuidanceControls
            onGuidanceChange={handleGuidanceChange}
            onClearWaypoint={handleClearWaypoint}
          />
        </aside>

        <div className="canvas-container" role="region" aria-label="Mundo de simulacion">
          <WorldRenderer
            worldWidth={2000}
            worldHeight={2000}
            onCreatureClick={handleCreatureClick}
            onBackgroundClick={handleBackgroundClick}
          />
          <Minimap
            worldWidth={2000}
            worldHeight={2000}
            onNavigate={handleMinimapNavigate}
          />
        </div>

        <aside
          className={`sidebar right ${mobileMenuOpen && activeMobileSidebar === 'right' ? 'mobile-visible' : ''}`}
          role="complementary"
          aria-label="Informacion y estadisticas"
        >
          <StatsPanel />
          <PlayerList onSelectCreature={handleCreatureClick} />
          <Leaderboard onSelectCreature={handleCreatureClick} />
          <CreaturePanel onDeselect={handleDeselect} onFollowToggle={handleFollowToggle} />
          <BrainVisualizer />
        </aside>
      </main>

      <footer className="footer" role="contentinfo">
        <span>GENESIS v0.2.0</span>
        <span aria-hidden="true">|</span>
        <span aria-label={`Ticks por segundo: ${actualTPS.toFixed(1)}`}>TPS: {actualTPS.toFixed(1)}</span>
        <span aria-hidden="true">|</span>
        <span aria-label={`Tick actual: ${tick}`}>Tick: {tick}</span>
      </footer>

      {/* Death Overlay - shows when user's creature dies */}
      <DeathOverlay onSpawn={handleSpawnMyCreature} />

      {/* Tutorial - shows for new users */}
      <Tutorial />

      {/* Keyboard shortcuts help modal */}
      <KeyboardHelp isOpen={showKeyboardHelp} onClose={() => setShowKeyboardHelp(false)} />
    </div>
  );
}

export default App;
